import React from 'react';

import { Button, Group, List, LoadingOverlay, Modal, Skeleton, Stack, Text, Title } from '@mantine/core';
import KiloAlert from 'components/KiloAlert/KiloAlert';
import useDeleteAppointmentType from 'hooks/appointmentType/useDeleteAppointmentType';
import useAffectedPackagesForEventType from 'hooks/package/useAffectedPackagesForEventType';
import { AppointmentType } from 'types';
import notificationHandler from 'utils/notifications';

interface IModalAppointmentTypeDeleteProps {
  opened: boolean;
  handleClose: () => void;
  appointmentType?: AppointmentType;
}

const SPEC_COPY =
  'Deleting this appointment type will remove it from any package attendance rules that reference it. ' +
  "This action can't be undone.";

const ModalAppointmentTypeDelete = ({
  opened,
  appointmentType,
  handleClose,
}: IModalAppointmentTypeDeleteProps): JSX.Element => {
  const { mutate, isLoading } = useDeleteAppointmentType();
  const { successNotification, errorNotification } = notificationHandler();

  const { data: affectedResponse, isLoading: isLoadingAffected } = useAffectedPackagesForEventType(
    opened ? appointmentType?.appointmentTypeId : undefined,
  );
  const affectedPackages = affectedResponse?.data.results ?? [];

  const handleDelete = (): void => {
    if (appointmentType) {
      mutate(appointmentType.appointmentTypeId, {
        onSuccess: () => {
          successNotification(
            'Appointment type delete success',
            `${appointmentType.name} successfully deleted.`,
          );
        },
        onError: error => {
          errorNotification('Appointment type delete failed', error.response?.data.message);
        },
        onSettled: () => {
          handleClose();
        },
      });
    }
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={handleClose}
      title={<Title order={4}>Delete Appointment Type</Title>}
      size='auto'
    >
      <LoadingOverlay visible={!appointmentType || isLoading} />
      <Stack>
        <Text>{`Are you sure you want to delete ${appointmentType?.name}?`}</Text>
        <Text color='dimmed'>{SPEC_COPY}</Text>
        {isLoadingAffected && <Skeleton height={60} />}
        {!isLoadingAffected && affectedPackages.length > 0 && (
          <KiloAlert variantStyle='containedWarning' title='Packages that reference this appointment type'>
            <List size='sm'>
              {affectedPackages.map(p => (
                <List.Item key={p.packageId}>{p.name}</List.Item>
              ))}
            </List>
          </KiloAlert>
        )}
        <Group position='right'>
          <Button onClick={handleDelete} color='red' loading={!appointmentType || isLoading}>
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModalAppointmentTypeDelete;
